"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { AuthUser } from "../types";

const AUTH_TOKEN_KEY = "astroji_auth_token_v2";

type CsatAuthContextValue = {
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (phone: string, token: string) => Promise<void>;
};

const CsatAuthContext = createContext<CsatAuthContextValue | undefined>(undefined);

const getValidSessionUser = (token: string): AuthUser | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload?.sub || payload.exp * 1000 <= Date.now()) return null;

    return { phone: payload.phone || payload.sub };
  } catch {
    return null;
  }
};

export function CsatAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const sessionUser = token ? getValidSessionUser(token) : null;

      if (sessionUser) {
        setUser(sessionUser);
      } else if (token) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }

      setIsLoading(false);
    }, 0);

    return () => window.clearTimeout(timer);
  }, []);

  const value = useMemo<CsatAuthContextValue>(() => ({
    isLoggedIn: user !== null,
    isLoading,
    async login(phone: string, token: string) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      setUser({ phone });
    },
  }), [isLoading, user]);

  return <CsatAuthContext.Provider value={value}>{children}</CsatAuthContext.Provider>;
}

export function useCsatAuth() {
  const context = useContext(CsatAuthContext);
  if (!context) {
    throw new Error("useCsatAuth must be used within CsatAuthProvider");
  }

  return context;
}
